<template>
    <section :class="['products-list row', {'products-list--partner-app':isPartnerApp}]">
        <div v-if="subCategories && subCategories.length && !loadingCategory"
            class="carousel-filter"
        >
            <chips-carousel :items="subCategories"
                valueKey="name"
                :isSelected="isSubCategorySelected"
                :isAllSelected="isAllSelected"
                :isAllDisabled="loadingProducts"
                :selectedMaxChips="selectedMaxChips"
                @filtersChanged="handleFiltersChange"
            />
        </div>
        <div v-if="variants && variants.length > 0 && !loadingProducts"
            class="products-list__container"
        >
            <product-card v-for="(variant, index) in variants"
                :id="`fanshop-products-product-tile-anchor${index}`"
                :key="`tile-product-${variant.id}`"
                :participationAmount="getParticipationAmount(variant)"
                :partnerId="variant.product.partnerId"
                :productImage="getImage(variant)"
                :productTitle="getTranslatedStr(variant, 'label', variant.label)"
                :ssuPrice="parseInt(variant.product.sellingPrice, 10)"
                :variantId="variant.id"
                :redeemableAt="getRedeemableDate(variant)"
                :variant="variant"
            />
            <h2 v-if="!loadingProducts && variants.length === 0"
                class="products-list__no-products"
            >
                {{ $t('rewards.fanShop.noProducts') }}
            </h2>
        </div>
        <h2 v-else-if="!loadingProducts"
            class="products-list__no-products"
        >
            {{ $t('rewards.fanShop.noProducts') }}
        </h2>
        <loading-spinner v-if="loadingProducts" />
        <pagination v-show="variants.length && !loadingProducts"
            id="products-pagination"
            class="products-list__pagination"
            :page="variantsPagination.currentPage"
            :last-page="variantsPagination.lastPage"
            @currentPage="onPaginationUpdate"
        />
    </section>
</template>
<script>
import {
    mapGetters,
} from 'vuex';

// Components
import productCard from '@webapp/modules/rewards/components/fanshop/product-card.vue';
import chipsCarousel from '@webapp/modules/rewards/components/fanshop/chips-carousel.vue';

// Local Types
import pagination from '@webapp/components/pagination/pagination';
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';
export default {
    name: 'fanshop-products-list',
    components: {
        pagination,
        productCard,
        loadingSpinner,
        chipsCarousel,
    },
    props: {
        variants: {
            type: Array,
            default: () => ([]),
            required: false,
        },
        subCategories: {
            type: Array,
            default: () => ([]),
            required: false,
        },
        loadingProducts: {
            type: Boolean,
            default: false,
            required: false,
        },
        loadingCategory: {
            type: Boolean,
            default: false,
            required: false,
        },
        selectedMaxChips: {
            type: Number,
            default: 0,
            required: false,
        },
    },
    data() {
        return {
            isInitialLoad: true,
            isResetPagination: false,
        };
    },
    computed: {
        ...mapGetters(['getTranslatedStr', 'isPartnerApp']),

        ...mapGetters('fanshop', [
            'variantsPagination',
            'productImagesSorted',
            'getSubCategoriesFilterById',
        ]),

        ...mapGetters('partners', ['partnerById', 'getAllPartnersFiltered']),

        ...mapGetters('wallet', ['decentralizedLedgerByToken']),

        isAllSelected() {
            return this.getSubCategoriesFilterById.length === 0;
        },
    },
    watch: {
        'getAllPartnersFiltered.default'() {
            this.$emit('update:productsByFilter', this.getSubCategoriesFilterById, 1);
        },
    },
    methods: {
        onPaginationUpdate(page) {
            // To avoid the double fetch on load
            if (this.isInitialLoad) {
                this.isInitialLoad = false;

                return;
            }

            if (this.isResetPagination && page === 1) {
                return;
            }
            this.isResetPagination = false;

            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth',
            });

            this.$emit('update:productsByPage', this.getSubCategoriesFilterById, page);
        },

        productImages(variant) {
            return this.productImagesSorted(variant, false) || [];
        },

        getImage(variant) {
            return this.productImages(variant).find((image) => image.imageType.type.includes('_image_desktop_'))?.url || '';
        },

        getRedeemableDate(variant) {
            return variant?.redeemableAt || null;
        },

        getParticipationAmount(variant) {
            const partner = this.partnerById(variant.product.partnerId);
            const {
                balance,
                lockedBalance,
            } = this.decentralizedLedgerByToken(partner.currencyId);
            const balanceAmount = parseFloat(balance) + parseFloat(lockedBalance) || 0;

            if (!variant.product?.productTierLevel) {

                return 0;
            }

            return balanceAmount >= variant.product?.productTierLevel?.minTokenRequired ? 0 : variant.product?.productTierLevel?.minTokenRequired;
        },

        isSubCategorySelected(subCategoryId) {
            return this.getSubCategoriesFilterById.some((selectedSubCategoryId) => subCategoryId === selectedSubCategoryId);
        },

        handleFiltersChange(subCategoryId) {
            let subCategories = [...this.getSubCategoriesFilterById];
            const subCategoryAlreadySelected = subCategories.some((selectedSubCategoryId) => subCategoryId === selectedSubCategoryId);

            if (subCategoryId === 'all') {
                subCategories = [];
            } else if (subCategoryAlreadySelected) {
                subCategories = subCategories.filter((id) => id !== subCategoryId);
            } else {
                subCategories.push(subCategoryId);
            }

            const searchParams = new URLSearchParams({
                selected: subCategories.join(','),
            });

            this.addHashToLocation(searchParams.toString(), subCategories?.length === 0);

            this.$emit('update:productsByFilter', subCategories);

            this.isResetPagination = true;
        },

        addHashToLocation(params, clear) {
            window.history.replaceState(
                {},
                null,
                clear ? this.$route.path : this.$route.path + '#' + params
            );
        },
    },
};
</script>

<style lang="scss" scoped>
.products-list {
    &__no-products {
        text-align: center;
    }

    &__container {
        display: grid;
        grid-template-columns: repeat(auto-fill, 46%);
        grid-gap: 2rem;
        justify-content: center;

        ::v-deep {
            .tile {
                max-width: 18rem;
                margin: auto;
            }
        }

        @include min-width(sm) {
            grid-gap: 4rem;
            grid-template-columns: repeat(auto-fill, 18rem);
            justify-content: start;
        }
    }

    .pagination {
        margin: 3.2rem 0;
    }

    ::v-deep .chips-carousel {
        margin: 3rem 0 3rem 0;
    }

    &--partner-app {
        padding: 2rem 0;

        @include min-width(md) {
            padding: auto;
        }

        .carousel-filter {
            display: none;
            @include min-width(md) {
                display: block;
            }
        }
    }
}
</style>